// src/useSectionAnalytics.js
//
// Tracks how long a section was actually visible in the viewport and fires
// a `section_view` GA4 event (via trackSectionView) each time the section
// becomes invisible again after having been seen. This is separate from
// useScrollReveal (which just toggles a CSS class) — this hook is purely
// for analytics and doesn't affect animation/visibility styling.
//
// Usage:
//   const ref = useSectionAnalytics("stats");
//   <section ref={ref} ...>
//
// If you're already using useScrollReveal's ref on the same element and
// can't attach two refs, use mergeRefs (exported below) to combine them:
//   const revealRef = useScrollReveal();
//   const analyticsRef = useSectionAnalytics("stats");
//   <section ref={mergeRefs(revealRef, analyticsRef)} ...>

import { useEffect, useRef } from "react";
import { trackSectionView } from "./utils/analytics";

export default function useSectionAnalytics(sectionName, options = {}) {
  const { threshold = 0.4 } = options;
  const elRef = useRef(null);
  const enteredAtRef = useRef(null);
  const firedForCurrentViewRef = useRef(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const flush = () => {
      if (enteredAtRef.current !== null) {
        const visibleMs = performance.now() - enteredAtRef.current;
        // Ignore extremely brief glimpses (e.g. fast scroll-through) —
        // under 300ms isn't a meaningful "view" for reporting purposes.
        if (visibleMs >= 300) {
          trackSectionView(sectionName, visibleMs);
        }
        enteredAtRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          enteredAtRef.current = performance.now();
          firedForCurrentViewRef.current = false;
        } else {
          flush();
        }
      },
      { threshold }
    );

    observer.observe(el);

    // Also flush if the user leaves/reloads the page while the section is
    // still visible, so we don't lose that data point.
    const handleUnload = () => flush();
    window.addEventListener("pagehide", handleUnload);

    return () => {
      flush();
      observer.disconnect();
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [sectionName, threshold]);

  return elRef;
}

// Small helper for attaching this hook's ref alongside another ref
// (e.g. from useScrollReveal) on the same DOM element.
export function mergeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(node);
      else ref.current = node;
    });
  };
}
